import { keyBy } from "lodash-es";

/**
 * Constants for voxel data storage
 */
export const VOXEL_EMPTY = 0; // Air/empty space

export const AIR_DENSITY = 0.5;

/**
 * Represents a terrain type in the game
 */
export type TerrainType = {
  // metadata:
  id: number;
  slug: string;
  name: string;

  // physics:
  /** if present, allows movement through this terrain type, but with a drag coefficient. Range: [0,1) where 0 is no drag, 1 is full drag. */
  movementDrag?: number;
  /** Density of the material. Used for buoyancy behavior. */
  density: number;

  // drilling:
  /** How hard it is to drill through. Range: (0,100] or `null` for unbreakable. */
  resistance: number | null;
  /** How much heat drilling this terrain type causes. Defaults to `resistance`. Range: [-100,100] */
  heatCause?: number;

  // terrain generation behavior:
  spawnType?: "ground" | "bedrock" | "mineral";
  /** Minimum depth where this terrain appears (for mineral|ground) */
  minDepth?: number;
  /** Rarity (for minerals only). 1 is most common. Range: (0,1] */
  rarity?: number;

  // for minerals/ores:
  /** Present for minable minerals/ores */
  sellValue?: number;

  // visuals:
  /** hex color */
  color: number;
  opacity?: number;
  /** Per-terrain roughness. Range: [0,1] */
  roughness: number;
  /** Per-terrain metalness. Range: [0,1] */
  metalness: number;
  /** size of Z dimension in world units */
  zLength: number;
};

let id = 1;
function nextId() {
  return id++;
}

// Terrain and mineral types remain the same as in the original implementation
const TERRAIN_TYPE_RAW_LIST = [
  {
    id: nextId(),
    slug: "dirt",
    name: "Dirt",
    spawnType: "ground",
    resistance: 1,
    density: 1.8, // lighter soil
    color: 0x8b4513,
    zLength: 50,
    roughness: 0.9,
    metalness: 0.1,
  },
  {
    id: nextId(),
    slug: "grass",
    name: "Grass",
    resistance: 1,
    density: 1.5, // light organic matter
    color: 0x228b22,
    zLength: 70,
    roughness: 1.0,
    metalness: 0.0,
  },
  {
    id: nextId(),
    slug: "sand",
    name: "Sand",
    resistance: 0.75,
    density: 2.2, // heavier than dirt
    color: 0xfaebd7,
    zLength: 50,
    roughness: 0.8,
    metalness: 0.1,
  },
  {
    id: nextId(),
    slug: "stone",
    name: "Stone",
    spawnType: "ground",
    resistance: 3,
    density: 2.7, // typical rock density
    color: 0x808080,
    zLength: 100,
    rarity: 0.9,
    minDepth: 50,
    roughness: 0.8,
    metalness: 0.2,
  },
  {
    id: nextId(),
    slug: "hard_stone",
    name: "Hard Stone",
    spawnType: "ground",
    resistance: 5,
    density: 3.0, // denser rock
    color: 0x505050,
    zLength: 120,
    rarity: 0.7,
    minDepth: 200,
    roughness: 0.7,
    metalness: 0.3,
  },
  {
    id: nextId(),
    slug: "bedrock",
    name: "Bedrock",
    spawnType: "bedrock",
    resistance: null,
    density: 3.5, // very dense
    color: 0x303030,
    zLength: 1000,
    minDepth: 500,
    roughness: 0.6,
    metalness: 0.4,
  },
  {
    id: nextId(),
    slug: "coal",
    name: "Coal",
    spawnType: "mineral",
    color: 0x2f4f4f,
    zLength: 50,
    minDepth: 10,
    rarity: 0.8,
    resistance: 1,
    density: 1.3, // coal is light
    sellValue: 10,
    roughness: 0.5,
    metalness: 0.5,
  },
  {
    id: nextId(),
    slug: "iron",
    name: "Iron",
    spawnType: "mineral",
    sellValue: 25,
    color: 0xa52a2a,
    zLength: 50,
    minDepth: 50,
    resistance: 1,
    density: 7.8, // iron density
    rarity: 0.6,
    roughness: 0.3,
    metalness: 0.7,
  },
  {
    id: nextId(),
    slug: "silver",
    name: "Silver",
    spawnType: "mineral",
    sellValue: 50,
    color: 0xc0c0c0,
    zLength: 50,
    minDepth: 150,
    resistance: 1,
    density: 10.5, // silver density
    rarity: 0.4,
    roughness: 0.2,
    metalness: 0.8,
  },
  {
    id: nextId(),
    slug: "gold",
    name: "Gold",
    spawnType: "mineral",
    sellValue: 100,
    color: 0xffd700,
    zLength: 50,
    minDepth: 300,
    resistance: 1,
    density: 19.3, // gold is very heavy
    rarity: 0.2,
    roughness: 0.1,
    metalness: 0.9,
  },
  {
    id: nextId(),
    slug: "diamond",
    name: "Diamond",
    spawnType: "mineral",
    sellValue: 250,
    color: 0xb9f2ff,
    zLength: 50,
    minDepth: 500,
    resistance: 1,
    density: 3.5, // diamond density
    rarity: 0.1,
    roughness: 0.05,
    metalness: 0.95,
  },
  {
    id: nextId(),
    slug: "platinum",
    name: "Platinum",
    spawnType: "mineral",
    sellValue: 350,
    color: 0xe5e4e2,
    zLength: 50,
    minDepth: 700,
    resistance: 1,
    density: 21.5, // platinum is extremely heavy
    rarity: 0.08,
    roughness: 0.05,
    metalness: 0.95,
  },
  {
    id: nextId(),
    slug: "uranium",
    name: "Uranium",
    spawnType: "mineral",
    sellValue: 500,
    color: 0x4ca64c,
    zLength: 50,
    minDepth: 900,
    resistance: 1,
    density: 19.0, // uranium density
    rarity: 0.05,
    roughness: 0.4,
    metalness: 0.6,
  },
  {
    id: nextId(),
    slug: "exotic_crystal",
    name: "Exotic Crystal",
    spawnType: "mineral",
    sellValue: 1000,
    color: 0xff00ff,
    zLength: 50,
    minDepth: 1200,
    resistance: 1,
    density: 4.0, // exotic, moderately heavy
    rarity: 0.02,
    roughness: 0.01,
    metalness: 0.99,
  },
  {
    id: nextId(),
    slug: "water",
    name: "Water",
    resistance: 0.5,
    heatCause: -5.0, // water should cool you down!
    movementDrag: 0.5,
    density: 1.0, // water is the reference density
    opacity: 0.75,
    color: 0x0000ff,
    zLength: 8, // = `voxelSize`
    minDepth: 0,
    roughness: 0.5,
    metalness: 0.5,
  },
  {
    id: nextId(),
    slug: "smoke",
    name: "Smoke",
    density: 0.2,
    color: 0x222222,
    opacity: 0.5,
    zLength: 5,
    roughness: 0.0,
    metalness: 0.0,
    resistance: null,
    movementDrag: 0,
  },
] as const satisfies TerrainType[];

type TerrainSlug = (typeof TERRAIN_TYPE_RAW_LIST)[number]["slug"];

export const TERRAIN_TYPE_LIST: readonly TerrainType[] = TERRAIN_TYPE_RAW_LIST;

export const TERRAIN_TYPES_MAP = keyBy(TERRAIN_TYPE_LIST, (d) => d.id);

export const TERRAIN_TYPES = Object.fromEntries(
  TERRAIN_TYPE_RAW_LIST.map((t) => [t.slug, t.id]),
) as Record<TerrainSlug, number>;

export const DEFAULT_GROUND_ID = TERRAIN_TYPES.dirt;
